import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BriefcaseBusiness } from 'lucide-react';
import { useFilterJobs } from '@/lib/api/hooks/job';
import { Loader } from '@/components/ui/Loader';
import DashboardCard from '@/pages/dashboard/DashboardCard';

interface RecentJobItem {
	id: string;
	title: string;
	date: string;
	author: string;
	deadline: string;
}

const RecentJobs = () => {
	const navigate = useNavigate();
	const { data: jobsData, isLoading, error } = useFilterJobs(
		{ page: 1, size: 3 },
		{ status: 'PUBLISH' }
	);

	if (error) {
		console.error('Error fetching jobs:', error);
	}

	const recentJobs = useMemo<RecentJobItem[]>(() => {
		if (error || !jobsData?.data?.data) return [];
		const jobList = Array.isArray(jobsData.data.data) ? jobsData.data.data : [];

		return jobList.map((job: { jobId: string; jobTitle: string; datePublished?: string; createdAt: string; username?: string; endDate: string }) => ({
			id: job.jobId,
			title: job.jobTitle,
			date: new Date(job.datePublished || job.createdAt).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
			author: job.username || 'Admin',
			deadline: new Date(job.endDate).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
		}));
	}, [jobsData, error]);

	return (
		<DashboardCard
			title="Recent Jobs"
			titleIcon={<BriefcaseBusiness className="h-4 w-4 text-gray-500" />}
			headerAction={
				<Link to="/admin/jobs" className="text-sm font-semibold text-[#681628] hover:underline">
					View all
				</Link>
			}
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader size="md" variant="wine" />
				</div>
			) : recentJobs.length > 0 ? (
				<div className="space-y-3">
					{recentJobs.map((job: RecentJobItem, index: number) => (
						<div
							key={job.id}
							className={`flex items-start justify-between gap-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors ${index < recentJobs.length - 1 ? 'pb-3 border-b border-gray-100' : ''}`}
							onClick={() => navigate(`/admin/jobs/${job.id}`)}
						>
							<div className="flex-1 min-w-0">
								<h3 className="font-semibold text-gray-800 text-xs mb-0.5 line-clamp-2">{job.title}</h3>
								<p className="text-xs text-gray-500">{job.date} | {job.author}</p>
							</div>
							<div className="text-right flex-shrink-0 min-w-[100px]">
								<p className="text-xs text-gray-500 mb-0.5">Deadline</p>
								<p className="text-xs font-semibold text-gray-900">{job.deadline}</p>
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="text-center py-8">
					<p className="text-sm text-gray-500">No recent jobs</p>
				</div>
			)}
		</DashboardCard>
	);
};

export default RecentJobs;